import RedFlagType from "../../entities/redFlagType";

/**
 * RedFlagType actions
 *
 * Call order:
 *
 * REDFLAGTYPE_SAVE_QUEUE:	adds the item to the save queue
 * --> REDFLAGTYPE_SAVE:	modifies the state
 * --> REDFLAGTYPE_SAVE_SUCCESS: if all went well
 * --> REDFLAGTYPE_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail REDFLAGTYPE_SAVE_QUEUE is during the call to REDFLAGTYPE_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at REDFLAGTYPE_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: REDFLAGTYPE_SAVE_SUCCESS, REDFLAGTYPE_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or REDFLAGTYPE_PENDING_CHECK (it left the queue).
 */

export const REDFLAGTYPE_FETCH_ONE_QUEUE: any = 'REDFLAGTYPE_FETCH_ONE_QUEUE';
export const REDFLAGTYPE_FETCH_SUCCESS: any = 'REDFLAGTYPE_FETCH_SUCCESS';
export const REDFLAGTYPE_FETCH_FAILURE: any = 'REDFLAGTYPE_FETCH_FAILURE';
export const REDFLAGTYPE_FETCH_ONE_SUCCESS: any = 'REDFLAGTYPE_FETCH_ONE_SUCCESS';
export const REDFLAGTYPE_FETCH_ONE_FAILURE: any = 'REDFLAGTYPE_FETCH_ONE_FAILURE';

export const REDFLAGTYPE_SAVE: any = 'REDFLAGTYPE_SAVE';
export const REDFLAGTYPE_SAVE_QUEUE: any = 'REDFLAGTYPE_SAVE_QUEUE';
export const REDFLAGTYPE_SAVE_SUCCESS: any = 'REDFLAGTYPE_SAVE_SUCCESS';
export const REDFLAGTYPE_SAVE_FAILURE: any = 'REDFLAGTYPE_SAVE_FAILURE';

export const REDFLAGTYPE_SAVE_MANY: any = 'REDFLAGTYPE_SAVE_MANY';
export const REDFLAGTYPE_SAVE_MANY_QUEUE: any = 'REDFLAGTYPE_SAVE_MANY_QUEUE';
export const REDFLAGTYPE_SAVE_MANY_SUCCESS: any = 'REDFLAGTYPE_SAVE_MANY_SUCCESS';
export const REDFLAGTYPE_SAVE_MANY_FAILURE: any = 'REDFLAGTYPE_SAVE_MANY_FAILURE';

export const REDFLAGTYPE_REMOVE: any = 'REDFLAGTYPE_REMOVE';
export const REDFLAGTYPE_REMOVE_QUEUE: any = 'REDFLAGTYPE_REMOVE_QUEUE';
export const REDFLAGTYPE_REMOVE_SUCCESS: any = 'REDFLAGTYPE_REMOVE_SUCCESS';
export const REDFLAGTYPE_REMOVE_FAILURE: any = 'REDFLAGTYPE_REMOVE_FAILURE';

export const REDFLAGTYPE_RESTORE: any = 'REDFLAGTYPE_RESTORE';
export const REDFLAGTYPE_RESTORE_QUEUE: any = 'REDFLAGTYPE_RESTORE_QUEUE';
export const REDFLAGTYPE_RESTORE_SUCCESS: any = 'REDFLAGTYPE_RESTORE_SUCCESS';
export const REDFLAGTYPE_RESTORE_FAILURE: any = 'REDFLAGTYPE_RESTORE_FAILURE';

export const REDFLAGTYPE_RESTORE_MANY: any = 'REDFLAGTYPE_RESTORE_MANY';
export const REDFLAGTYPE_REMOVE_MANY: any = 'REDFLAGTYPE_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const REDFLAGTYPE_SAVE_REVERT: any = 'REDFLAGTYPE_SAVE_REVERT';
export const REDFLAGTYPE_PENDING_CHECK: any = 'REDFLAGTYPE_PENDING_CHECK';

/* Fetch actions */
export const fetchRedFlagTypeSuccess = (redFlagTypes: RedFlagType[], partial: boolean, lastFetch: number) => ({type: REDFLAGTYPE_FETCH_SUCCESS, redFlagTypes, partial, lastFetch});
export const fetchRedFlagTypeFailure = (err: any) => {
		console.error('RedFlagTypeAction: fetchRedFlagTypeFailure', err);
		return {type: REDFLAGTYPE_FETCH_FAILURE, err};
};
export const fetchOneRedFlagTypeQueue = (redFlagTypeId: string, sentAddedOn?: number) => ({type: REDFLAGTYPE_FETCH_ONE_QUEUE, redFlagTypeId, sentAddedOn});
export const fetchOneRedFlagTypeSuccess = (redFlagType: RedFlagType | null, sentAddedOn?: number, redFlagTypeId: string = redFlagType?.id) => ({type: REDFLAGTYPE_FETCH_ONE_SUCCESS, redFlagTypeId, redFlagType, sentAddedOn});
export const fetchOneRedFlagTypeFailure = (id: string, err: any) => {
		console.error('RedFlagTypeAction: fetchOneRedFlagTypeFailure', err);
		return {type: REDFLAGTYPE_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveRedFlagType = (redFlagType: RedFlagType) => {
    // console.debug('⮂ REDFLAGTYPE_SAVE action', redFlagType?.id);
    return {type: REDFLAGTYPE_SAVE, redFlagType};
}
export const saveRedFlagTypeQueue = (redFlagType: RedFlagType, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ REDFLAGTYPE_SAVE_QUEUE action', redFlagType?.id);
    return {type: REDFLAGTYPE_SAVE_QUEUE, redFlagType, onQueued};
}
export const saveRedFlagTypeSuccess = (redFlagType: RedFlagType, sentAddedOn?: number) => {
    // console.debug('⮂ REDFLAGTYPE_SAVE_SUCCESS action', redFlagType?.id);
    return {type: REDFLAGTYPE_SAVE_SUCCESS, redFlagType, sentAddedOn};
}
export const saveRedFlagTypeFailure = (redFlagType: RedFlagType, status: number, message: string) => {
    console.warn(`⮂ REDFLAGTYPE_SAVE_FAILURE error saving redFlagType: HTTP ${status}: ${message}`, redFlagType?.id);
    return {type: REDFLAGTYPE_SAVE_FAILURE, redFlagType, status, message};
};

/* SaveMany actions */
export const saveManyRedFlagTypes = (redFlagTypes: RedFlagType[]) => {
    // console.debug('⮂ REDFLAGTYPE_SAVE_MANY action', redFlagTypes?.map(redFlagType => redFlagType?.id).join(','));
    return {type: REDFLAGTYPE_SAVE_MANY, redFlagTypes};
}
export const saveManyRedFlagTypesQueue = (redFlagTypes: RedFlagType[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ REDFLAGTYPE_SAVE_MANY_QUEUE action', redFlagTypes?.map(redFlagType => redFlagType.id).join(','));
    return {type: REDFLAGTYPE_SAVE_MANY_QUEUE, redFlagTypes, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `redFlagTypes` (OWA-1018) */
export const saveManyRedFlagTypesSuccess = (redFlagTypes: RedFlagType[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ REDFLAGTYPE_SAVE_MANY_SUCCESS action', redFlagTypes?.map(redFlagType => redFlagType.id).join(','));
    return {type: REDFLAGTYPE_SAVE_MANY_SUCCESS, redFlagTypes, sentAddedOns};
}
export const saveManyRedFlagTypesFailure = (redFlagTypes: RedFlagType[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_REDFLAGTYPES_FAILURE Error saving redFlagTypes: HTTP ${status}: ${message}`, redFlagTypes?.map(redFlagType => redFlagType.id).join(','));
		return {type: REDFLAGTYPE_SAVE_MANY_FAILURE, redFlagTypes, status, message};
};

/* Remove actions */
export const removeRedFlagType = (redFlagType: RedFlagType) => ({ type: REDFLAGTYPE_REMOVE, redFlagType });
export const removeRedFlagTypeQueue = (redFlagType: RedFlagType) => ({ type: REDFLAGTYPE_REMOVE_QUEUE, redFlagType });
export const removeRedFlagTypeSuccess = (redFlagType: RedFlagType) => ({ type: REDFLAGTYPE_REMOVE_SUCCESS, redFlagType });
export const removeRedFlagTypeFailure = (redFlagType: RedFlagType, status: number, message: string) => {
	console.error(`Redux: removeRedFlagTypeFailure Error removing redFlagType: HTTP ${status}: ${message}`);
	return ({ type: REDFLAGTYPE_REMOVE_FAILURE, redFlagType, status, message });
};

/* Restore actions */
export const restoreRedFlagType = (redFlagType: RedFlagType) => ({ type: REDFLAGTYPE_RESTORE, redFlagType });
export const restoreRedFlagTypeQueue = (redFlagType: RedFlagType) => ({ type: REDFLAGTYPE_RESTORE_QUEUE, redFlagType });
export const restoreRedFlagTypeSuccess = (redFlagType: RedFlagType, sentAddedOn?: number) => ({ type: REDFLAGTYPE_RESTORE_SUCCESS, redFlagType, sentAddedOn });
export const restoreRedFlagTypeFailure = (redFlagType: RedFlagType, status: number, message: string) => {
	console.error(`Redux: restoreRedFlagTypeFailure Error restoring redFlagType: HTTP ${status}: ${message}`);
	return ({ type: REDFLAGTYPE_RESTORE_FAILURE, redFlagType, status, message });
};

/* RestoreMany actions */
export const restoreManyRedFlagTypes = (redFlagTypes: RedFlagType[]) => ({ type: REDFLAGTYPE_RESTORE_MANY, redFlagTypes });

/* RemoveMany actions */
export const removeManyRedFlagTypes = (redFlagTypes: RedFlagType[]) => ({ type: REDFLAGTYPE_REMOVE_MANY, redFlagTypes });

/* Pending save actions (OWA-1018) */
/**
 * The save `redFlagType` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertRedFlagTypeSave = (redFlagType: RedFlagType, options: {sentAddedOn?: number, current?: RedFlagType | null, keep?: boolean} = {}) =>
    ({type: REDFLAGTYPE_SAVE_REVERT, redFlagType, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkRedFlagTypePendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: REDFLAGTYPE_PENDING_CHECK, live, refused, readAt});